import React from "react";
import Modal from "react-bootstrap/Modal";
import { OrderItem } from "../../context/OrderContext";
import { MenuItem, CustomSelectionsWithSections } from "./pos.types";
import { getPizzaIcon, computeFreeToppingDiscount } from "./pos.utils";
import { useAuth } from "../../context/AuthContext";

export interface CustomizeModalProps {
  isOpen: boolean;
  selectedCustomizeItem: MenuItem | null;
  customizingItem: OrderItem | null;
  customSelections: CustomSelectionsWithSections;
  specialRequests: string;
  attemptedSubmit: boolean;
  collapsedGroups: Record<string, boolean>;
  cart: OrderItem[];
  menuItems: MenuItem[];
  missingRequiredGroupName: string | null;
  customizeGroupRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  onClose: () => void;
  onChoiceChange: (key: string, qty: number) => void;
  onHalfHalfSelection: (
    key: string,
    section: "left" | "right" | "whole",
    minQty?: number,
  ) => void;
  onCustomSubmit: () => void;
  onSpecialRequestsChange: (val: string) => void;
  onAttemptedSubmitChange: (val: boolean) => void;
  onCollapsedGroupsChange: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  onGoToNextGroup: (idx: number) => void;
  onScrollToMissingGroup: () => void;
  setCustomSelections: React.Dispatch<
    React.SetStateAction<CustomSelectionsWithSections>
  >;
  onAddCrossItem: (item: MenuItem) => void;
  resolveCategoryCode: (item: {
    categoryCode?: string;
    categoryId?: string;
  }) => string | undefined;
  setOrderData: React.Dispatch<React.SetStateAction<any>>;
  // Deal overlays
  activeBundleDeal?: {
    dealName: string;
    bundlePrice: number;
    slots: { name?: string; options: string[]; preselections: { groupName: string; choiceLabel: string }[] }[];
    currentSlotIndex: number;
    pricingMode?: "fixed" | "cheapest_free" | "percent_off";
    discountPercent?: number;
  } | null;
  activeBYODeal?: {
    dealName: string;
    basePrice: number;
    includedToppings: number;
    minPizzas: number;
    pizzasDone: number;
  } | null;
  onSwitchBundleOption?: (itemId: string) => void;
}

const CustomizeModal: React.FC<CustomizeModalProps> = ({
  isOpen,
  selectedCustomizeItem,
  customSelections,
  specialRequests,
  attemptedSubmit,
  collapsedGroups,
  cart,
  menuItems,
  missingRequiredGroupName,
  customizeGroupRefs,
  onClose,
  onChoiceChange,
  onHalfHalfSelection,
  onCustomSubmit,
  onSpecialRequestsChange,
  onAttemptedSubmitChange,
  onCollapsedGroupsChange,
  onGoToNextGroup,
  onScrollToMissingGroup,
  setCustomSelections,
  onAddCrossItem,
  resolveCategoryCode,
  setOrderData,
  activeBundleDeal,
  activeBYODeal,
  onSwitchBundleOption,
}) => {
  const { currencySymbol } = useAuth();

  if (!selectedCustomizeItem) return null;

  const isHalfHalfPizza =
    selectedCustomizeItem.category?.toLowerCase().includes("pizza") ||
    selectedCustomizeItem.customize.some((grp) => grp.portionOptions);

  const currentItemCategory = selectedCustomizeItem?.category?.toLowerCase();
  const cartItemIds = new Set(cart.map((c) => c.itemId));

  let recommendCategories: string[] = [];
  if (
    currentItemCategory === "pizza" ||
    currentItemCategory === "pasta" ||
    currentItemCategory === "burgers" ||
    currentItemCategory === "sandwiches" ||
    currentItemCategory === "main course"
  ) {
    recommendCategories = [
      "drinks",
      "beverages",
      "sides",
      "appetizers",
      "desserts",
      "dessert",
    ];
  } else if (
    currentItemCategory === "appetizers" ||
    currentItemCategory === "sides"
  ) {
    recommendCategories = ["drinks", "beverages", "desserts", "dessert"];
  } else {
    recommendCategories = [
      "drinks",
      "beverages",
      "desserts",
      "dessert",
      "sides",
      "appetizers",
    ];
  }

  const crossSellItems = menuItems
    .filter(
      (item) =>
        recommendCategories.includes(item.category?.toLowerCase() || "") &&
        !cartItemIds.has(item._id) &&
        item._id !== selectedCustomizeItem?._id,
    )
    .slice(0, 6);

  const lockedKeys = new Set<string>(
    activeBundleDeal
      ? (activeBundleDeal.slots[activeBundleDeal.currentSlotIndex]?.preselections || []).map(
          (p) => `${p.groupName}|||${p.choiceLabel}`
        )
      : []
  );

  // Pre-compute how many BYO free toppings remain at the START of each customize group.
  // This mirrors the greedy deduction used in POS.tsx when the item is added to cart.
  const byoFreeAtGroupStart: number[] = [];
  if (activeBYODeal && selectedCustomizeItem) {
    let rem = activeBYODeal.includedToppings;
    for (const group of selectedCustomizeItem.customize) {
      byoFreeAtGroupStart.push(rem);
      if (!group.required && !group.multiple) {
        for (const choice of group.choices) {
          if (choice.default || rem <= 0) continue;
          const k = `${group.name}|||${choice.label}`;
          const qty = Number((customSelections as Record<string, number>)[k] || 0);
          if (qty > 0) rem -= Math.min(qty, rem);
        }
      }
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={() => {
        onCollapsedGroupsChange({});
        onClose();
      }}
      dialogClassName="!max-w-[640px] flex items-center justify-center h-screen my-0"
      contentClassName="
  bg-white dm-customize-modal
  sm:w-[480px]
  h-[90%]
  p-0
  shadow-[0_6px_18px_rgba(0,0,0,0.12)]
  overflow-y-auto no-scrollbar
  relative
  !rounded-3xl
"
    >
      <Modal.Body className="p-0 rounded-t-3xl">
        {/* Sticky Header with Close Button */}
        <div className="sticky top-0 z-30 bg-white">
          <button
            onClick={() => {
              onCollapsedGroupsChange({});
              onClose();
            }}
            className="absolute top-4 right-4 bg-main/90 text-white font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-black"
          >
            ✕
          </button>
        </div>

        <div className="relative">
          <img
            src={selectedCustomizeItem.imageUrl || "/placeholder.png"}
            alt={selectedCustomizeItem.name}
            className="w-full rounded-3xl object-cover"
            onError={(e) => {
              e.currentTarget.src = "/placeholder.png";
            }}
          />
        </div>

        {/* Bundle deal strip */}
        {activeBundleDeal && (
          <div className="dm-deal-strip bg-orange-50 border-y border-orange-200 px-3 py-2">
            <div className="flex items-center justify-between mb-0">
              <p className="text-xs font-bold text-orange-700 mb-0">🎁 {activeBundleDeal.dealName}</p>
              <p className="text-xs font-semibold text-orange-700 mb-0">
                {(() => {
                  const priceText =
                    activeBundleDeal.pricingMode === "cheapest_free"
                      ? "Cheapest free"
                      : activeBundleDeal.pricingMode === "percent_off"
                        ? `${activeBundleDeal.discountPercent}% off`
                        : `${currencySymbol}${activeBundleDeal.bundlePrice.toFixed(2)}`;
                  const stepName = activeBundleDeal.slots[activeBundleDeal.currentSlotIndex]?.name;
                  return activeBundleDeal.slots.length > 1
                    ? `Step ${activeBundleDeal.currentSlotIndex + 1}/${activeBundleDeal.slots.length}${stepName ? ` · ${stepName}` : ""} · ${priceText}`
                    : `${stepName ? `${stepName} · ` : ""}${priceText}`;
                })()}
              </p>
            </div>
          </div>
        )}

        {/* BYO deal strip */}
        {activeBYODeal && activeBYODeal.includedToppings > 0 && (
          <div className="bg-blue-50 border-y border-blue-200 px-3 py-2">
            <p className="text-xs font-bold text-blue-700 mb-0">🎁 {activeBYODeal.dealName}</p>
            <p className="text-xs text-blue-600 mt-0.5 mb-0">
              Pizza {activeBYODeal.pizzasDone + 1} of {activeBYODeal.minPizzas} · {currencySymbol}{activeBYODeal.basePrice.toFixed(2)} · First {activeBYODeal.includedToppings} toppings free
            </p>
          </div>
        )}

        <div className="p-3">
          <h3 className="font-bold text-lg text-black mb-[10px] text-left mt-2">
            {selectedCustomizeItem.name}
          </h3>
          <p
            className="text-sm text-black font-outfit h-auto overflow-hidden
                      text-ellipsis [display:-webkit-box] [-webkit-box-orient:vertical] text-left mt-2"
          >
            {selectedCustomizeItem.description}
          </p>

          {selectedCustomizeItem.customize.map((group, gIndex) => {
            const freeToppingCount = group.noOfFreeToppings ?? 0;
            // In BYO mode, free toppings come from the deal budget, not the item's own noOfFreeToppings
            const effectiveFreeCount = (activeBYODeal && !group.required && !group.multiple)
              ? (byoFreeAtGroupStart[gIndex] ?? 0)
              : freeToppingCount;
            const maxToppings = group.maxToppingsAllowed;
            const minAddons = group.minAddons ?? 0;

            const totalSelectedInGroup = (group.choices || []).reduce(
              (total, c) => {
                if (c.default) return total;
                const k = `${group.name}|||${c.label}`;
                return total + Math.max(0, Number(customSelections[k] || 0));
              },
              0,
            );

            const availableFreePerChoice = new Map<string, number>();
            let freeUsedInGroup = 0;
            if (effectiveFreeCount > 0) {
              let rem = effectiveFreeCount;
              for (const c of group.choices) {
                if (c.default) continue;
                const k = `${group.name}|||${c.label}`;
                availableFreePerChoice.set(k, rem);
                const qty = Number(customSelections[k] || 0);
                if (qty > 0 && rem > 0) {
                  const freeQty = Math.min(qty, rem);
                  freeUsedInGroup += freeQty;
                  rem -= freeQty;
                }
              }
            }

            const hasSelection = (group.choices || []).some((choice) => {
              const key = `${group.name}|||${choice.label}`;
              return Number(customSelections[key] || 0) > 0;
            });
            const minAddonsMet =
              minAddons === 0 || totalSelectedInGroup >= minAddons;
            const showError =
              attemptedSubmit &&
              ((group.required && !hasSelection) ||
                (minAddons > 0 && !minAddonsMet));
            const needsSelectionBadge = group.required || minAddons > 0;
            const isSelected = group.required
              ? hasSelection
              : minAddonsMet;

            const isCollapsed = collapsedGroups[group.name] ?? false;

            const preselectedChoiceInGroup = group.choices.find((c) =>
              lockedKeys.has(`${group.name}|||${c.label}`)
            );
            const preselectedPrice = preselectedChoiceInGroup?.price ?? 0;

            return (
              <div
                key={gIndex}
                className={`mb-3 border-b-2 rounded-lg p-2 transition-all duration-200 ${
                  showError ? "border-2 border-red-500" : ""
                }`}
                ref={(el) => {
                  customizeGroupRefs.current[group.name] = el;
                }}
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-2 mb-2 cursor-pointer select-none group"
                  onClick={() =>
                    onCollapsedGroupsChange((prev) => ({
                      ...prev,
                      [group.name]: !isCollapsed,
                    }))
                  }
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-semibold">
                      {group.name}
                    </span>
                    {needsSelectionBadge && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          isSelected
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {isSelected ? "Selected" : "Required"}
                      </span>
                    )}
                    {!group.required && effectiveFreeCount > 0 && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          {freeUsedInGroup}/{effectiveFreeCount} free
                        </span>
                      )}
                    {!group.required &&
                      maxToppings !== undefined &&
                      maxToppings > 0 && (
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            totalSelectedInGroup >= maxToppings
                              ? "bg-red-100 text-red-600"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {totalSelectedInGroup}/{maxToppings} max
                        </span>
                      )}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`shrink-0 text-gray-400 group-hover:text-gray-600 transition-transform duration-300 ${
                      isCollapsed ? "-rotate-90" : "rotate-0"
                    }`}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                <div
                  className={`grid transition-all duration-300 ease-in-out ${isCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]"}`}
                >
                  <div className="overflow-hidden">
                    <div className="grid grid-rows">
                      {group.multiple === false && group.required === true
                        ? [...group.choices]
                            .sort(
                              (a, b) =>
                                (b.default ? 1 : 0) - (a.default ? 1 : 0),
                            )
                            .map((choice, idx) => {
                              const key = `${group.name}|||${choice.label}`;
                              const quantity =
                                (customSelections[key] as number) || 0;
                              const selectedSection =
                                (customSelections[
                                  `${key}_section`
                                ] as string) || "";
                              const isPreselected = lockedKeys.has(key);
                              const isDisabledByPrice =
                                !!preselectedChoiceInGroup &&
                                (choice.price ?? 0) < preselectedPrice;

                              const showHalfHalfOptions =
                                isHalfHalfPizza &&
                                !group.name.toLowerCase().includes("dip") &&
                                !group.name.toLowerCase().includes("sauce") &&
                                !group.name.toLowerCase().includes("drink") &&
                                !group.name.toLowerCase().includes("side") &&
                                !group.name.toLowerCase().includes("sizes") &&
                                !group.name
                                  .toLowerCase()
                                  .includes("bake level") &&
                                !group.name.toLowerCase().includes("base");

                              return (
                                <div key={idx} className={isDisabledByPrice ? "opacity-40" : ""}>
                                  <div
                                    className="flex items-center gap-1 p-1 mb-1"
                                    style={{ cursor: isDisabledByPrice ? "not-allowed" : "pointer" }}
                                    onClick={() => {
                                      if (isDisabledByPrice) return;
                                      group.choices.forEach((c) => {
                                        const otherKey = `${group.name}|||${c.label}`;
                                        onChoiceChange(otherKey, 0);
                                      });
                                      onChoiceChange(key, 1);
                                      onCollapsedGroupsChange((prev) => ({ ...prev, [group.name]: true }));
                                      onGoToNextGroup(gIndex);
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name={group.name}
                                      checked={quantity === 1}
                                      onChange={() => {}}
                                      style={{ pointerEvents: "none" }}
                                      className="
                            w-4 h-4 cursor-pointer accent-black
                            appearance-none rounded-full border border-black
                            checked:border-black checked:bg-black
                            focus:outline-none focus:ring-0 focus:ring-offset-0
                          "
                                    />
                                    <div className="flex items-center w-full space-x-3">
                                      {choice.image && (
                                        <img
                                          src={choice.image}
                                          alt={choice.label}
                                          className="w-14 h-14 object-cover rounded-md"
                                        />
                                      )}
                                      <div className="flex flex-row w-full justify-between">
                                        <p className="text-sm font-medium m-0 cursor-pointer">
                                          {choice.label}
                                          {selectedSection && (
                                            <span className="text-xs text-black ml-1">
                                              -{" "}
                                              {selectedSection
                                                .charAt(0)
                                                .toUpperCase() +
                                                selectedSection.slice(1)}
                                            </span>
                                          )}
                                          {isPreselected && quantity === 1 && (
                                            <span className="text-xs text-gray-500 ml-1">(Included)</span>
                                          )}
                                        </p>
                                        <p className="text-sm font-medium m-0">
                                          {choice.price > 0 && !(isPreselected && quantity === 1) &&
                                            `${currencySymbol}${choice.price.toFixed(2)}`}
                                          {isPreselected && quantity === 1 && (
                                            <span className="text-xs text-green-600 font-medium">Included</span>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                        : group.multipleAddons
                          ? group.choices.map((choice, idx) => {
                              const key = `${group.name}|||${choice.label}`;
                              const qty =
                                (customSelections[key] as number) || 0;
                              const isMaxed =
                                !!maxToppings &&
                                totalSelectedInGroup >= maxToppings;
                              return (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between p-1.5 gap-2"
                                >
                                  <div className="flex items-center gap-3">
                                    {choice.image && (
                                      <img
                                        src={choice.image}
                                        alt={choice.label}
                                        className="w-12 h-12 object-cover rounded-md"
                                      />
                                    )}
                                    <div>
                                      <p className="text-sm font-medium m-0">
                                        {choice.label}
                                      </p>
                                      {choice.price > 0 && (
                                        <p className="text-xs text-gray-500 m-0">
                                          +{currencySymbol}{choice.price.toFixed(2)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {qty > 0 && (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            onChoiceChange(key, qty - 1)
                                          }
                                          className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-lg font-semibold hover:bg-gray-100 hover:text-black transition"
                                        >
                                          −
                                        </button>
                                        <span className="w-5 text-center text-sm font-semibold">
                                          {qty}
                                        </span>
                                      </>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        !isMaxed &&
                                        onChoiceChange(key, qty + 1)
                                      }
                                      disabled={isMaxed}
                                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-lg font-semibold disabled:opacity-30 hover:bg-gray-100 hover:text-black transition"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          : group.multiple
                            ? [...group.choices]
                                .sort(
                                  (a, b) =>
                                    (b.default ? 1 : 0) - (a.default ? 1 : 0),
                                )
                                .map((choice, idx) => {
                                  const key = `${group.name}|||${choice.label}`;
                                  const isChecked =
                                    ((customSelections[key] as number) || 0) >
                                    0;
                                  const isChoiceMaxedOut =
                                    !!maxToppings &&
                                    maxToppings > 0 &&
                                    totalSelectedInGroup >= maxToppings &&
                                    !isChecked;
                                  const reachedMaxWithNewSelection =
                                    !!maxToppings &&
                                    maxToppings > 0 &&
                                    !isChecked &&
                                    totalSelectedInGroup < maxToppings &&
                                    totalSelectedInGroup + 1 >= maxToppings;
                                  const selectedSection =
                                    (customSelections[
                                      `${key}_section`
                                    ] as string) || "";

                                  const showHalfHalfOptions =
                                    isHalfHalfPizza &&
                                    (group.showPortionSelector !== undefined
                                      ? group.showPortionSelector
                                      : !group.name
                                          .toLowerCase()
                                          .includes("dip") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("sauce") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("drink") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("side") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("sizes") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("base"));

                                  const portionOptionsMultiple = group
                                    .portionOptions?.length
                                    ? group.portionOptions
                                    : [
                                        { key: "left", label: "Left" },
                                        { key: "right", label: "Right" },
                                        { key: "whole", label: "Whole" },
                                      ];

                                  const handleCheckboxToggle = () => {
                                    if (isChecked) {
                                      onChoiceChange(key, 0);
                                      return;
                                    }
                                    if (isChoiceMaxedOut) return;
                                    onChoiceChange(key, 1);
                                    if (reachedMaxWithNewSelection) {
                                      // ✅ Collapse and go next when max reached
                                      onCollapsedGroupsChange((prev) => ({ ...prev, [group.name]: true }));
                                      onGoToNextGroup(gIndex);
                                    }
                                  };

                                  const handlePortionToggle = (
                                    section: "left" | "right" | "whole"
                                  ) => {
                                    if (!isChecked && isChoiceMaxedOut) return;
                                    onHalfHalfSelection(key, section);
                                    if (reachedMaxWithNewSelection) {
                                      onCollapsedGroupsChange((prev) => ({ ...prev, [group.name]: true }));
                                      onGoToNextGroup(gIndex);
                                    }
                                  };

                                  return (
                                    <div
                                      key={idx}
                                      className={`flex items-center p-1 gap-1 w-full ${
                                        isChoiceMaxedOut
                                          ? "opacity-50"
                                          : "opacity-100"
                                      }`}
                                    >
                                      {showHalfHalfOptions ? (
                                        <div className="flex flex-col w-full space-y-2">
                                          <div className="flex items-center w-full space-x-3">
                                            {choice.image && (
                                              <img
                                                src={choice.image}
                                                alt={choice.label}
                                                className="w-14 h-14 object-cover rounded-md"
                                              />
                                            )}
                                            <div className="flex flex-col w-full mt-1">
                                              <div className="flex flex-row w-full justify-between mb-2">
                                                <p className="text-sm font-medium m-0">
                                                  {choice.label}
                                                  {selectedSection && (
                                                    <span className="text-xs text-black ml-1">
                                                      -{" "}
                                                      {selectedSection
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        selectedSection.slice(
                                                          1,
                                                        )}
                                                    </span>
                                                  )}
                                                </p>
                                                <p className="text-sm font-medium m-0">
                                                  {choice.price > 0 &&
                                                    `${currencySymbol}${choice.price.toFixed(2)}`}
                                                </p>
                                              </div>
                                            </div>
                                            <div className="flex gap-2 mt-0">
                                              {portionOptionsMultiple.map(
                                                (option) => (
                                                  <button
                                                    key={option.key}
                                                    onClick={() =>
                                                      handlePortionToggle(
                                                        option.key as
                                                          | "left"
                                                          | "right"
                                                          | "whole",
                                                      )
                                                    }
                                                    disabled={isChoiceMaxedOut}
                                                  className={`flex items-center justify-center gap-1 rounded-lg border-2 text-xs font-semibold transition-all duration-200 w-[60px] h-[45px] shadow-sm ${
  selectedSection === option.key
    ? "border-black bg-black text-white shadow-md scale-105 dm-customize-selected"
    : isChoiceMaxedOut
      ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
      : "border-gray-300 bg-white text-black hover:border-black/50 hover:bg-gray-100 hover:shadow-md transform hover:scale-105"
}`}
                                                  >
                                                    {getPizzaIcon(
                                                      option.key as
                                                        | "left"
                                                        | "right"
                                                        | "whole",
                                                      selectedSection ===
                                                        option.key,
                                                    )}
                                                    <span className="font-bold">
                                                      {option.label}
                                                    </span>
                                                  </button>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="flex items-center p-1 gap-1 w-full">
                                            <label className="relative flex items-center cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={isChecked}
                                                disabled={isChoiceMaxedOut}
                                                onChange={handleCheckboxToggle}
                                                className="
                                                w-4 h-4 cursor-pointer accent-black rounded border border-black/20
                                                focus:outline-none focus:ring-0 disabled:cursor-not-allowed
                                              "
                                              />
                                              <svg
                                                className="absolute w-3 h-3 text-white pointer-events-none left-[3px] top-[3px] opacity-0 peer-checked:opacity-100"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="white"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M16.707 5.293a1 1 0 010 1.414l-7.364 7.364a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414l4.121 4.121 6.657-6.657a1 1 0 011.414 0z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            </label>
                                            <div className="flex items-center w-full space-x-3">
                                              {choice.image && (
                                                <img
                                                  src={choice.image}
                                                  alt={choice.label}
                                                  className="w-14 h-14 object-cover rounded-md"
                                                />
                                              )}
                                              <div className="flex flex-row w-full justify-between">
                                                <p
                                                  className="text-sm font-medium m-0 cursor-pointer"
                                                  onClick={handleCheckboxToggle}
                                                >
                                                  {choice.label}
                                                </p>
                                                <p className="text-sm font-medium m-0">
                                                  {choice.price > 0 &&
                                                    `${currencySymbol}${choice.price.toFixed(2)}`}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                })
                            : [...group.choices]
                                .sort(
                                  (a, b) =>
                                    (b.default ? 1 : 0) - (a.default ? 1 : 0),
                                )
                                .map((choice, idx) => {
                                  const key = `${group.name}|||${choice.label}`;
                                  const quantity =
                                    (customSelections[key] as number) || 0;
                                  const selectedSection =
                                    (customSelections[
                                      `${key}_section`
                                    ] as string) || "";

                                  const showHalfHalfOptions =
                                    isHalfHalfPizza &&
                                    (group.showPortionSelector !== undefined
                                      ? group.showPortionSelector
                                      : !group.name
                                          .toLowerCase()
                                          .includes("dip") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("sauce") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("drink") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("side") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("sizes") &&
                                        !group.name
                                          .toLowerCase()
                                          .includes("base"));

                                  const quantityOptions = group.quantityOptions
                                    ?.length
                                    ? group.quantityOptions
                                    : [0, 1, 2, 3];
                                  const portionOptions = group.portionOptions
                                    ?.length
                                    ? group.portionOptions
                                    : [
                                        { key: "left", label: "Left" },
                                        { key: "whole", label: "Whole" },
                                        { key: "right", label: "Right" },
                                      ];

                                  if (choice.default) {
                                    return (
                                      <div key={idx} className="">
                                        <div className="flex items-center space-x-3 mb-2">
                                          {choice.image && (
                                            <img
                                              src={choice.image}
                                              alt={choice.label}
                                              className="w-14 h-14 object-cover border-[1.5px] border-black/20 rounded-md"
                                            />
                                          )}
                                          <div className="flex flex-col w-full">
                                            <div className="flex flex-row w-full justify-between">
                                              <p className="text-m font-bold line-clamp-2 mb-0">
                                                {choice.label}{" "}
                                                {selectedSection &&
                                                  quantity > 0 && (
                                                    <span className="text-xs text-black/70 ml-1">
                                                      -{" "}
                                                      {selectedSection
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        selectedSection.slice(
                                                          1,
                                                        )}
                                                    </span>
                                                  )}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-3 flex-wrap">
                                          {quantityOptions.map((num) => {
                                            const displayNum =
                                              num === -1 ? 0 : num;
                                            const actualQuantity =
                                              choice.default && displayNum === 0
                                                ? -1
                                                : num;
                                            const additionalPrice = Math.max(
                                              0,
                                              actualQuantity -
                                                (choice.default ? 1 : 0),
                                            ) * (choice.price || 0);
                                            const isSelected =
                                              quantity === actualQuantity;

                                            return (
                                              <button
                                                key={num}
                                                onClick={() => {
                                                  if (showHalfHalfOptions) {
                                                    setCustomSelections(
                                                      (prev) => {
                                                        const prevSection =
                                                          (prev[
                                                            `${key}_section`
                                                          ] as string) || "";
                                                        return {
                                                          ...prev,
                                                          [key]: Math.max(
                                                            actualQuantity,
                                                            -1,
                                                          ),
                                                          [`${key}_section`]:
                                                            actualQuantity <= 0
                                                              ? ""
                                                              : prevSection ||
                                                                "whole",
                                                        };
                                                      },
                                                    );
                                                    return;
                                                  }
                                                  onChoiceChange(
                                                    key,
                                                    actualQuantity,
                                                  );
                                                }}
                                                className={`grid place-items-center rounded-lg border transition-all w-[60px] h-[45px] mb-3 ${
                                                  isSelected
                                                    ? "border-black bg-black text-white dm-customize-selected"
                                                    : "border-black/30 bg-white text-black"
                                                }`}
                                              >
                                                <div className="flex flex-col items-center justify-center">
                                                  <span className="text-lg font-bold leading-none">
                                                    {num}
                                                  </span>
                                                  {additionalPrice > 0 && (
                                                    <span
                                                      className={`text-xs mt-1 ${isSelected ? "text-white" : "text-black"}`}
                                                    >
                                                      +$
                                                      {additionalPrice.toFixed(
                                                        2,
                                                      )}
                                                    </span>
                                                  )}
                                                </div>
                                              </button>
                                            );
                                          })}
                                        </div>
                                        {showHalfHalfOptions &&
                                          quantity > 0 && (
                                            <div>
                                              <div className="flex mb-4 border-2 border-black/20 rounded-lg overflow-hidden w-fit">
                                                {portionOptions.map(
                                                  (option, index) => (
                                                    <div
                                                      key={option.key}
                                                      className={`flex ${
                                                        index !==
                                                        portionOptions.length -
                                                          1
                                                          ? "border-r border-black/20"
                                                          : ""
                                                      }`}
                                                    >
                                                      <button
                                                        onClick={() =>
                                                          onHalfHalfSelection(
                                                            key,
                                                            option.key as
                                                              | "left"
                                                              | "right"
                                                              | "whole",
                                                            1,
                                                          )
                                                        }
                     className={`flex flex-col items-center justify-center gap-0.5 rounded-lg text-[10px] font-semibold transition-all duration-200 w-[60px] h-[45px]
${
  selectedSection === option.key
    ? "bg-black text-white scale-105 border-x border-black dm-customize-selected"
    : "bg-white text-black hover:bg-gray-100 hover:scale-105 hover:border-x border-black/30"
}`}
                                                      >
                                                        {getPizzaIcon(
                                                          option.key as
                                                            | "left"
                                                            | "right"
                                                            | "whole",
                                                          selectedSection ===
                                                            option.key,
                                                        )}
                                                        <span className="font-bold">
                                                          {option.label}
                                                        </span>
                                                      </button>
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    );
                                  } else {
                                    const availableFree =
                                      availableFreePerChoice.get(key) ?? 0;
                                    const isFree =
                                      quantity > 0 && availableFree >= quantity;
                                    const isChoiceMaxedOut =
                                      !!maxToppings &&
                                      totalSelectedInGroup >= maxToppings &&
                                      quantity === 0;
                                    if (isChoiceMaxedOut) return null;

                                    return (
                                      <div key={idx} className="my-2">
                                        <div className="flex items-center space-x-3 mb-2">
                                          {choice.image && (
                                            <img
                                              src={choice.image}
                                              alt={choice.label}
                                              className="w-14 h-14 object-cover border-[1.5px] border-black/20 rounded-md"
                                            />
                                          )}
                                          <div className="flex flex-col w-full mt-1">
                                            <div className="flex flex-row w-full justify-between">
                                              <p className="text-m font-bold line-clamp-2 mb-0">
                                                {choice.label}{" "}
                                                {selectedSection &&
                                                  quantity > 0 && (
                                                    <span className="text-xs text-gray-500 ml-1">
                                                      -{" "}
                                                      {selectedSection
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        selectedSection.slice(
                                                          1,
                                                        )}
                                                    </span>
                                                  )}
                                              </p>
                                              {isFree && quantity > 0 && (
                                                <span className="text-xs font-semibold text-green-600 ml-2">
                                                  FREE
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-3 flex-wrap mb-3">
                                          {quantityOptions.map((num) => {
                                            const actualQuantity = num;
                                            const freeUnitsForQty = Math.min(
                                              actualQuantity,
                                              availableFree,
                                            );
                                            const additionalPrice = Math.max(
                                              0,
                                              actualQuantity - freeUnitsForQty,
                                            ) * choice.price;
                                            const isSelected =
                                              quantity === actualQuantity;
                                            const isButtonDisabled =
                                              !!maxToppings &&
                                              num > 0 &&
                                              totalSelectedInGroup -
                                                quantity +
                                                num >
                                                maxToppings;

                                            return (
                                              <button
                                                key={num}
                                                disabled={isButtonDisabled}
                                                onClick={() => {
                                                  if (isButtonDisabled) return;
                                                  const nextTotalInGroup =
                                                    totalSelectedInGroup -
                                                    quantity +
                                                    actualQuantity;
                                                  const reachedMaxNow =
                                                    !!maxToppings &&
                                                    maxToppings > 0 &&
                                                    totalSelectedInGroup <
                                                      maxToppings &&
                                                    nextTotalInGroup >=
                                                      maxToppings;
                                                  if (showHalfHalfOptions) {
                                                    setCustomSelections(
                                                      (prev) => {
                                                        const prevSection =
                                                          (prev[
                                                            `${key}_section`
                                                          ] as string) || "";
                                                        return {
                                                          ...prev,
                                                          [key]: Math.max(
                                                            actualQuantity,
                                                            -1,
                                                          ),
                                                          [`${key}_section`]:
                                                            actualQuantity <= 0
                                                              ? ""
                                                              : actualQuantity ===
                                                                  1
                                                                ? "whole"
                                                                : prevSection,
                                                        };
                                                      },
                                                    );
                                                    if (reachedMaxNow) {
                                                      onGoToNextGroup(gIndex);
                                                    }
                                                    return;
                                                  }
                                                  onChoiceChange(
                                                    key,
                                                    actualQuantity,
                                                  );
                                                  if (reachedMaxNow) {
                                                    onGoToNextGroup(gIndex);
                                                  }
                                                }}
                                                className={`grid place-items-center rounded-lg border transition-all w-[60px] h-[45px] ${
                                                  isButtonDisabled
                                                    ? "border-black/10 bg-gray-50 text-black/20 cursor-not-allowed"
                                                    : isSelected
                                                      ? "border-black bg-black text-white dm-customize-selected"
                                                      : "border-black/30 bg-white text-black"
                                                }`}
                                              >
                                                <div className="flex flex-col items-center justify-center">
                                                  <span className="text-lg font-bold leading-none">
                                                    {num}
                                                  </span>
                                                  {additionalPrice > 0 && (
                                                    <span
                                                      className={`text-xs mt-1 ${isSelected ? "text-white" : "text-gray-600"}`}
                                                    >
                                                      +$
                                                      {additionalPrice.toFixed(
                                                        2,
                                                      )}
                                                    </span>
                                                  )}
                                                </div>
                                              </button>
                                            );
                                          })}
                                        </div>
                                        {showHalfHalfOptions &&
                                          quantity > 0 && (
                                            <div>
                                              <div className="flex mb-4 border-2 border-gray-300 rounded-lg overflow-hidden w-fit">
                                                {portionOptions.map(
                                                  (option, index) => (
                                                    <div
                                                      key={option.key}
                                                      className={`flex ${
                                                        index !==
                                                        portionOptions.length -
                                                          1
                                                          ? "border-r border-gray-300"
                                                          : ""
                                                      }`}
                                                    >
                                                      <button
                                                        onClick={() =>
                                                          onHalfHalfSelection(
                                                            key,
                                                            option.key as
                                                              | "left"
                                                              | "right"
                                                              | "whole",
                                                          )
                                                        }
                               className={`flex flex-col items-center justify-center gap-0.5 rounded-lg text-[10px] font-semibold transition-all duration-200 w-[60px] h-[45px]
${
  selectedSection === option.key
    ? "bg-black text-white scale-105 border-x border-black dm-customize-selected"
    : "bg-white text-black hover:bg-gray-100 hover:scale-105 hover:border-x border-black/30"
}`}
                                                      >
                                                        {getPizzaIcon(
                                                          option.key as
                                                            | "left"
                                                            | "right"
                                                            | "whole",
                                                          selectedSection ===
                                                            option.key,
                                                        )}
                                                        <span className="font-bold">
                                                          {option.label}
                                                        </span>
                                                      </button>
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    );
                                  }
                                })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Special Requests */}
          {/* <div>
            <h1 className="text-lg font-semibold mb-0">Special requests</h1>
            <span className="text-sm font-medium text-gray-600">
              We'll try our best to accommodate requests, but can't make
              changes that affect pricing.
            </span>
            <textarea
              maxLength={280}
              value={specialRequests}
              onChange={(e) => onSpecialRequestsChange(e.target.value)}
              className="mt-2 w-full h-[75px] resize-none overflow-y-auto rounded-md border border-black/30 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="Add special request"
            />
          </div> */}

          {/* Cross-sell: Recommended for you */}
          {crossSellItems.length > 0 && (
            <div className="mt-4">
              <h1 className="text-lg font-semibold mb-2">
                Recommended for you
              </h1>
              <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-2">
                {crossSellItems.map((crossItem) => {
                  const isInCart = cart.some((c) => c.itemId === crossItem._id);

                  return (
                    <div
                      key={crossItem._id}
                      className="flex-shrink-0 w-[90px] bg-white rounded-lg border border-black/20 p-2 shadow-sm"
                    >
                      <img
                        src={crossItem.imageUrl || "/placeholder.png"}
                        alt={crossItem.name}
                        className="w-full h-12 object-contain rounded-md mb-2"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            "/placeholder.png";
                        }}
                      />
                      <p className="text-xs font-medium text-black line-clamp-2 mb-1 h-8 text-center">
                        {crossItem.name}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-black">
                          {currencySymbol}{Number(crossItem.price).toFixed(2)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isInCart) return;
                            const newCartItem = {
                              cartId: `${crossItem._id}-${Date.now()}`,
                              itemId: crossItem._id,
                              name: crossItem.name,
                              kitchenName: crossItem.kitchenName,
                              price: crossItem.price,
                              quantity: 1,
                              imageUrl: crossItem.imageUrl,
                              customOptions: [],
                              isPartOfDeal: false,
                              categoryId:
                                resolveCategoryCode(crossItem) ??
                                crossItem.categoryId ??
                                "",
                            };
                            setOrderData((prev: any) => {
                              if (!prev) return prev;
                              return {
                                ...prev,
                                items: [...(prev.items || []), newCartItem],
                              };
                            });
                          }}
                          disabled={isInCart}
                          className={`text-xs font-bold px-2 py-1 rounded-md transition-all ${
                            isInCart
                              ? "bg-main text-white cursor-not-allowed"
                              : "bg-main text-white "
                          }`}
                        >
                          {isInCart ? "✓" : "+"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="sticky bottom-0 bg-white m-0 rounded-b-3xl border-t border-black/10">
        <div className="flex justify-between w-full items-center">
          <div className="text-lg font-semibold">
            {activeBundleDeal ? `Extras: ${currencySymbol}` : `Total: ${currencySymbol}`}
            {(() => {
              const basePrice = activeBundleDeal
                ? 0
                : activeBYODeal
                  ? activeBYODeal.basePrice
                  : (selectedCustomizeItem?.price || 0);

              let extraPrice = Object.entries(customSelections).reduce(
                (acc, [key, quantity]) => {
                  if (key.endsWith("_section")) return acc;
                  // Locked preselections are covered by bundle price — skip entirely
                  if (activeBundleDeal && lockedKeys.has(key)) return acc;
                  const [groupName, choiceLabel] = key.split("|||");
                  const group = selectedCustomizeItem?.customize.find(
                    (g) => g.name === groupName,
                  );
                  const choice = group?.choices.find(
                    (c) => c.label === choiceLabel,
                  );
                  const qty = Number(quantity || 0);
                  const unitPrice = choice?.price || 0;
                  const chargeableQty =
                    qty > 0 && choice?.default
                      ? Math.max(0, qty - 1)
                      : Math.max(0, qty);
                  return acc + unitPrice * chargeableQty;
                },
                0,
              );

              // BYO: free toppings come from the deal's includedToppings, not the item's own
              if (activeBYODeal && selectedCustomizeItem) {
                let freeRem = activeBYODeal.includedToppings;
                let byoDiscount = 0;
                for (const group of selectedCustomizeItem.customize) {
                  if (group.required || group.multiple || freeRem <= 0) continue;
                  for (const choice of group.choices) {
                    if (freeRem <= 0) break;
                    if (choice.default) continue;
                    const key = `${group.name}|||${choice.label}`;
                    const qty = Number((customSelections as Record<string, number>)[key] || 0);
                    if (qty > 0) {
                      const freeQty = Math.min(qty, freeRem);
                      byoDiscount += (choice.price || 0) * freeQty;
                      freeRem -= freeQty;
                    }
                  }
                }
                extraPrice = Math.max(0, extraPrice - byoDiscount);
              } else if (!activeBYODeal && selectedCustomizeItem) {
                extraPrice = Math.max(0, extraPrice - computeFreeToppingDiscount(selectedCustomizeItem, customSelections));
              }

              return (basePrice + extraPrice).toFixed(2);
            })()}
          </div>
          <div
            className={`dm-add-to-cart-btn border border-black/30 py-1.5 sm:py-2 px-3 sm:px-[14px] rounded-[8px] sm:rounded-[10px]
font-semibold text-[11px] sm:text-[12px] md:text-[13px] transition-colors ${
              missingRequiredGroupName
                ? "dm-add-to-cart-btn-disabled bg-gray-400/90 text-white cursor-not-allowed"
                : "dm-add-to-cart-btn-enabled bg-main/90 text-white cursor-pointer"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (missingRequiredGroupName) {
                onAttemptedSubmitChange(true);
                onScrollToMissingGroup();
                return;
              }
              onAttemptedSubmitChange(false);
              onCustomSubmit();
              onCollapsedGroupsChange({});
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.delete("item");
              window.history.pushState({}, "", currentUrl.toString());
            }}
          >
            {activeBundleDeal && activeBundleDeal.currentSlotIndex < activeBundleDeal.slots.length - 1
              ? `Next (${activeBundleDeal.currentSlotIndex + 1}/${activeBundleDeal.slots.length})`
              : "Add to Cart"}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomizeModal;
